import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AvailabilityFormModal from '@/components/AvailabilityFormModal';
import { useAuth } from '@/hooks/useAuth';
import { useAvailableCandidates, type AvailableCandidateInput } from '@/hooks/useAvailableCandidates';
import { lumi } from '@/lib/lumi';
import {
  trackAvailabilityDepositOpen,
  trackAvailabilityDepositDraftSaved,
  trackAvailabilityDepositPublishedDirect,
} from '@/lib/analytics';

// Clé sessionStorage partagée avec Dashboard.tsx pour finaliser la publication
// du profil de disponibilité après création / connexion du compte candidat.
export const PENDING_AVAILABILITY_DRAFT_KEY = 'bnssa-mns:pending-availability-draft';

/**
 * useAvailabilityDepositFlow — Flux mutualisé de dépôt de disponibilité candidat.
 *
 * Même principe que le dépôt d'annonce recruteur (useJobDepositFlow) :
 * Étape 1 : ouverture directe de la pop-up de disponibilité (AvailabilityFormModal),
 *           que le visiteur soit connecté ou non.
 * Étape 2 : à l'envoi du formulaire :
 *   • candidat connecté → upsert immédiat du profil puis redirection vers le
 *     dashboard (onglet « Mes disponibilités ») ;
 *   • visiteur non connecté → le profil est stocké en brouillon (sessionStorage)
 *     et l'utilisateur est redirigé vers l'inscription candidat ; le dashboard
 *     finalise automatiquement la publication à la première connexion.
 */
export function useAvailabilityDepositFlow() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isRecruiter, isOrganisme, isAdmin } = useAuth();
  const { upsert, saving } = useAvailableCandidates();
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => {
    // Garde de rôle : un recruteur ou un organisme connecté ne peut pas déposer
    // une disponibilité candidat. Les candidats connectés, les visiteurs non
    // connectés et l'admin restent autorisés.
    if (isAuthenticated && !isAdmin && (isRecruiter || isOrganisme)) {
      toast.error(
        isOrganisme
          ? "Votre compte est un compte organisme de formation. Seuls les candidats peuvent indiquer leurs disponibilités."
          : "Votre compte est un compte recruteur. Seuls les candidats peuvent indiquer leurs disponibilités."
      );
      return;
    }
    trackAvailabilityDepositOpen({ authenticated: isAuthenticated && !!user?.userId });
    setOpen(true);
  }, [isAuthenticated, user, isRecruiter, isOrganisme, isAdmin]);
  const closeModal = useCallback(() => setOpen(false), []);

  const handleSave = useCallback(
    async (data: AvailableCandidateInput) => {
      // Cas 1 : candidat connecté → publication immédiate
      if (isAuthenticated && user?.userId) {
        try {
          const saved = await upsert({ ...data, creatorEmail: user.email || data.email }, user.userId);
          setOpen(false);
          // Best-effort : notifier les recruteurs si profil dispo immédiate publié
          if (saved && saved._id && saved.isPublished !== false && saved.isAvailableNow === true) {
            void lumi.functions
              .invoke('notifyCandidatePublished', { method: 'POST', body: { candidateId: saved._id } })
              .catch((err: unknown) =>
                console.warn('[useAvailabilityDepositFlow] notify failed (non bloquant):', err)
              );
          }
          trackAvailabilityDepositPublishedDirect({
            qualification: data.qualification,
            region: data.region,
            isAvailableNow: data.isAvailableNow === true,
          });
          toast.success('Votre profil est publié dans la CVthèque !');
          navigate('/dashboard?tab=disponible');
        } catch {
          toast.error("Impossible de publier votre profil. Réessayez.");
        }
        return;
      }
      // Cas 2 : non connecté → brouillon + inscription candidat
      try {
        sessionStorage.setItem(PENDING_AVAILABILITY_DRAFT_KEY, JSON.stringify(data));
      } catch {
        // silent
      }
      trackAvailabilityDepositDraftSaved({
        qualification: data.qualification,
        region: data.region,
        isAvailableNow: data.isAvailableNow === true,
      });
      setOpen(false);
      toast('Créez votre compte candidat pour finaliser votre profil de disponibilité.', { icon: '📝' });
      navigate('/inscription?role=candidate&next=/dashboard?tab=disponible');
    },
    [isAuthenticated, user, upsert, navigate]
  );

  const submitLabel = isAuthenticated && user?.userId ? 'Publier mon profil' : 'Continuer';

  // Repli : si l'utilisateur a déjà soumis la pop-up (brouillon de pré-inscription
  // dans sessionStorage) puis revient sur la pop-up sans avoir créé son compte,
  // on réinjecte ces données pour ne jamais afficher un formulaire vide.
  let fallbackDraft: Record<string, unknown> | null = null;
  if (open) {
    try {
      const raw = sessionStorage.getItem(PENDING_AVAILABILITY_DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        // L'email est stocké sous `email` dans le draft pending ; la pop-up lit
        // `contactEmail`. On mappe pour restaurer correctement le champ.
        if (parsed && parsed.email && !parsed.contactEmail) {
          parsed.contactEmail = parsed.email;
        }
        fallbackDraft = parsed;
      }
    } catch {
      fallbackDraft = null;
    }
  }

  const modal = open ? (
    <AvailabilityFormModal
      email={user?.email}
      fallbackDraft={fallbackDraft}
      onClose={closeModal}
      onSave={handleSave}
      submitLabel={submitLabel}
      saving={saving}
    />
  ) : null;

  return { open, openModal, closeModal, modal };
}
